import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  ShellMenuRowContent,
  UserChip,
  shellMenuRowClass,
} from '@realtyfeed/ui';
import { useState, type ReactNode } from 'react';
import { LogOut, User } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { AppShellUser } from '../type';

export type UserMenuProps = {
  user: AppShellUser;
  onSignOut: () => void;
  profilePath: string;
};

/**
 * The navbar's account menu: the user chip, and the Profile / Log out pair it opens.
 *
 * Lifted out of `Navbar` because it is the only piece of the navbar with state of its own.
 *
 * ### Why the row class goes on the item, not on the child
 *
 * `shellMenuRowClass()` is passed as the *item's* `className`, so `cn()` inside
 * `DropdownMenuItem` twMerges it against that component's own defaults and the row's `px-2 py-3`
 * deterministically replaces the item's `px-3 py-2`. Put on the `asChild` child instead, the two
 * class strings are merely concatenated by Radix's `Slot` and which padding wins falls to the
 * order Tailwind happened to emit `p-*` in.
 *
 * ### One panel, not two
 *
 * The source component in `rf-mls-vendor-dashboard-v2` branched on an `accountSwitcher` prop into
 * the design system's "Your Accounts" panel. That branch is gone here, along with
 * `AccountSwitcherPanel`, `AccountSwitcherMenuContent` and the `AppShellAccountSwitcher` type:
 * this console is admin/internal and **not vendor-scoped**. There is no "current account" to
 * switch between and no memberships endpoint behind one. See docs/07-decisions.md D13.
 *
 * The caret's rotation and `aria-expanded` come off the same `open` state that drives the panel,
 * and the panel is width-matched to the trigger via `--radix-dropdown-menu-trigger-width` so the
 * two read as one control.
 */
export const UserMenu = ({ user, onSignOut, profilePath }: UserMenuProps): ReactNode => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <UserChip
          open={open}
          user={{ name: user.name, email: user.email, avatarUrl: user.avatarUrl }}
        />
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="min-w-[var(--radix-dropdown-menu-trigger-width)] p-2"
      >
        <DropdownMenuItem asChild className={shellMenuRowClass()}>
          <Link to={profilePath}>
            <ShellMenuRowContent icon={<User size={18} />} label={t('nav.profile')} />
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        <DropdownMenuItem asChild className={shellMenuRowClass()}>
          {/* A button, not a link: signing out is an action, and `asChild` keeps
              Radix's own `onSelect` closing the menu around it. */}
          <button type="button" onClick={onSignOut}>
            <ShellMenuRowContent icon={<LogOut size={18} />} label={t('nav.logout')} />
          </button>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
